// import external libraries
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// import constants
import { BACKLOG_PREFERENCES_STORAGE_KEY } from './Backlog.consts';

// import types
import type {
  BacklogEmployeeViewFilters,
  BacklogPreferencesState,
  BacklogPreferencesStore
} from './Backlog.types';

/**
 * What the takenlijst opens on until the planner changes something: no narrowing filters, the signed
 * difference in the cells, and both the totals column and the fulfilled weeks shown. The same defaults
 * the page has always had — now the starting point a first visit falls back to, and what a reset
 * restores to.
 */
export const BACKLOG_PREFERENCES_DEFAULTS: BacklogPreferencesState = {
  rowEntityType: 'CLIENT',
  organizationalUnitIds: [],
  entityFilters: [],
  employeeViewFilters: { functions: [] },
  cellValueMode: 'difference',
  showTotals: true,
  showCompleted: true
};

/**
 * v1 persisted `activityCodes`/`careReceiverIds`, which version 2 replaces with
 * `entityFilters`/`activityIds` — drop the stale keys rather than carry them forward unused, and seed
 * the new ones empty rather than guess a mapping from the old shape. v3 adds the row entity type, which
 * neither earlier version had: the page only ever showed care receivers, so that is what a remembered
 * selection means. v4 adds the employee view's own filters, which no earlier version remembered at all, so
 * they start empty.
 *
 * v5 drops `activityIds` and the remembered employees — the latter were numeric cost-centre ids where an
 * employee pick now carries an entity uuid, so there is no conversion to make — and lifts `planUnitIds`
 * out of `employeeViewFilters` to the top level, keeping its stored value.
 *
 * v6 renames that key to `organizationalUnitIds`, carrying the stored ids over: dropping them would empty
 * every planner's remembered plan-unit selection on the first load after the upgrade.
 *
 * Exported so the migration itself is unit-testable without going through zustand's rehydration timing.
 */
export const migrateBacklogPreferences = (
  persisted: unknown,
  fromVersion: number
) => {
  const stored = (persisted ?? {}) as Record<string, unknown>;
  const withRowEntityType =
    fromVersion < 3 ? { ...stored, rowEntityType: 'CLIENT' } : stored;
  const withEmployeeViewFilters =
    fromVersion < 4
      ? {
          ...withRowEntityType,
          employeeViewFilters: { functions: [] }
        }
      : withRowEntityType;

  const beforeV5 =
    fromVersion < 2
      ? (() => {
          const {
            activityCodes: _activityCodes,
            careReceiverIds: _careReceiverIds,
            ...rest
          } = withEmployeeViewFilters;
          return { ...rest, entityFilters: [] };
        })()
      : withEmployeeViewFilters;

  const beforeV6 =
    fromVersion < 5
      ? (() => {
          const {
            activityIds: _activityIds,
            employeeViewFilters: storedEmployeeViewFilters,
            ...rest
          } = beforeV5 as Record<string, unknown>;
          const employeeView = (storedEmployeeViewFilters ?? {}) as Record<
            string,
            unknown
          >;
          return {
            ...rest,
            // Carried up rather than reset, so an upgrade does not lose a plan unit the planner had set.
            planUnitIds: Array.isArray(employeeView.planUnitIds)
              ? employeeView.planUnitIds
              : [],
            employeeViewFilters: {
              functions: Array.isArray(employeeView.functions)
                ? employeeView.functions
                : []
            }
          };
        })()
      : (beforeV5 as Record<string, unknown>);

  if (fromVersion < 6) {
    const { planUnitIds: storedPlanUnitIds, ...rest } = beforeV6;
    // The key was renamed, not dropped: a v5 entry's remembered ids move across under the new name.
    return {
      ...rest,
      organizationalUnitIds: Array.isArray(storedPlanUnitIds)
        ? storedPlanUnitIds
        : []
    };
  }

  return beforeV6;
};

/**
 * The persisted state folded onto the defaults, with every collection filled in.
 *
 * Zustand's own merge is shallow, so a stored object <em>replaces</em> its default rather than filling the
 * gaps in it: a stored {@code employeeViewFilters} written before a field existed — or written with one
 * undefined, which {@code toFormValues} will happily hand back — arrives with that key missing. The page
 * then reads it as an array and the whole takenlijst fails to render: {@code organizationalUnitIds.includes} on
 * undefined, before a single request goes out, so the planner gets a blank page and nothing to go on.
 *
 * {@link migrateBacklogPreferences} cannot catch this, because it only runs when the stored version differs
 * — and a shape can rot within one version. This runs on every rehydration, so stored state that is already
 * broken is repaired on the next load rather than needing the planner to clear their browser storage.
 */
export const mergeBacklogPreferences = (
  persisted: unknown,
  current: BacklogPreferencesStore
): BacklogPreferencesStore => {
  const stored = (persisted ?? {}) as Partial<BacklogPreferencesState>;
  // Partial, because that is the whole point: a stored shape can be missing any of these.
  const storedEmployeeViewFilters: Partial<BacklogEmployeeViewFilters> =
    stored.employeeViewFilters ?? {};

  return {
    ...current,
    ...stored,
    entityFilters:
      stored.entityFilters ?? BACKLOG_PREFERENCES_DEFAULTS.entityFilters,
    // Top-level since v5, under this name since v6. An older shape is lifted and renamed by
    // migrateBacklogPreferences; this only fills the gap when the key is missing outright.
    organizationalUnitIds:
      stored.organizationalUnitIds ??
      BACKLOG_PREFERENCES_DEFAULTS.organizationalUnitIds,
    employeeViewFilters: {
      functions:
        storedEmployeeViewFilters.functions ??
        BACKLOG_PREFERENCES_DEFAULTS.employeeViewFilters.functions
    }
  };
};

/**
 * Remembers which planning entity is on screen and the filter and view-settings popovers across visits, so
 * a planner does not set them again every time the page opens. Persisted to localStorage.
 *
 * A remembered activity or care receiver code is safe to keep even after it leaves the data: the page
 * matches the stored ids against whatever the current response offers, so a code that no longer appears
 * simply stops narrowing rather than hiding everything.
 */
export const useBacklogPreferencesStore = create<BacklogPreferencesStore>()(
  persist(
    (set) => ({
      ...BACKLOG_PREFERENCES_DEFAULTS,
      setRowEntityType: (rowEntityType) => set({ rowEntityType }),
      setOrganizationalUnitIds: (organizationalUnitIds) =>
        set({ organizationalUnitIds }),
      setEntityFilters: (entityFilters) => set({ entityFilters }),
      setEmployeeViewFilters: (employeeViewFilters) =>
        set({ employeeViewFilters }),
      setCellValueMode: (cellValueMode) => set({ cellValueMode }),
      setShowTotals: (showTotals) => set({ showTotals }),
      setShowCompleted: (showCompleted) => set({ showCompleted })
    }),
    {
      name: BACKLOG_PREFERENCES_STORAGE_KEY,
      version: 6,
      // Only the settings, never the setters — persisting the whole state would write functions that
      // JSON drops anyway.
      partialize: ({
        rowEntityType,
        organizationalUnitIds,
        entityFilters,
        employeeViewFilters,
        cellValueMode,
        showTotals,
        showCompleted
      }) => ({
        rowEntityType,
        organizationalUnitIds,
        entityFilters,
        employeeViewFilters,
        cellValueMode,
        showTotals,
        showCompleted
      }),
      migrate: migrateBacklogPreferences,
      merge: mergeBacklogPreferences
    }
  )
);
